import {useMemo, useState} from 'react';
import {Sparkles, X} from 'lucide-react';
import {cn} from '@/matic/lib/utils/cn';
import {
  AssociationPicker,
  useInlineCreateAssociation,
  type AssociationPickerSelection,
} from '@/matic/features/relay/components/shared/AssociationPicker';
import {buildAssociationRef} from '@/matic/features/relay/utils/association-ref';

/**
 * An AI-suggested association (carries a confidence). Mirrors the
 * `FileAssociationSuggestion` shape returned by the file-upload suggestion API.
 */
export type SuggestedAssociation = AssociationPickerSelection & {
  confidence: number;
};

/**
 * The kept union of associations across all three chip sources, in the shape
 * the create-file commit path expects.
 */
export type SelectedAssociation = {
  objectTypeId: string;
  /** Null = type-only ("Category") association. */
  objectRecordId: string | null;
};

/**
 * Composite chip identity. Type-only ("Category") selections have a null
 * record id, so keying on the record id alone would collide every type-only
 * chip onto one key — the type id disambiguates.
 */
const chipKey = (chip: {objectTypeId: string; objectRecordId: string | null}) =>
  buildAssociationRef(chip.objectTypeId, chip.objectRecordId);

export type AssociationSelectionController = {
  presetChips: AssociationPickerSelection[];
  suggestedChips: SuggestedAssociation[];
  manualChips: AssociationPickerSelection[];
  selectedAssociations: SelectedAssociation[];
  add: (selection: AssociationPickerSelection) => void;
  removePreset: (key: string) => void;
  removeSuggested: (key: string) => void;
  removeManual: (key: string) => void;
};

/**
 * Three-source association selection: **preset** (existing context — an object
 * tab's current record, a chat anchor/scope), **suggested** (AI, carries a
 * confidence), and **manual** (added via {@link AssociationPicker}). All chips
 * are removable; the kept union is exposed as `selectedAssociations`.
 *
 * Suggestions are additive on top of the preset and de-duped against it (and
 * against manual picks), so a suggestion that equals an already-present
 * association isn't shown twice — only the *new* cross-type ones surface.
 */
export function useAssociationSelection(options: {
  preset: AssociationPickerSelection[];
  suggested?: SuggestedAssociation[];
  /**
   * Record ids already attached via a non-removable baseline (e.g. a legacy
   * `associations` prop). Suggestions matching these are hidden so the same
   * association isn't offered twice.
   */
  excludeRecordIds?: string[];
  /** Suggestions below this confidence are hidden (backend already drops < 0.5). */
  confidenceThreshold?: number;
}): AssociationSelectionController {
  const {
    preset,
    suggested = [],
    excludeRecordIds = [],
    confidenceThreshold = 0.5,
  } = options;

  const excludedIds = useMemo(
    () => new Set(excludeRecordIds),
    [excludeRecordIds]
  );

  const [presetRemoved, setPresetRemoved] = useState<Set<string>>(new Set());
  const [suggestedRemoved, setSuggestedRemoved] = useState<Set<string>>(
    new Set()
  );
  const [manual, setManual] = useState<AssociationPickerSelection[]>([]);

  // Original preset ids — used to de-dupe suggestions against the preset even
  // after a preset chip is removed (removing a preset chip means "drop it",
  // not "let the suggestion resurrect it").
  const presetIds = useMemo(
    () => new Set(preset.map((p) => chipKey(p))),
    [preset]
  );

  const presetChips = useMemo(
    () => preset.filter((p) => !presetRemoved.has(chipKey(p))),
    [preset, presetRemoved]
  );

  const manualIds = useMemo(
    () => new Set(manual.map((m) => chipKey(m))),
    [manual]
  );

  const suggestedChips = useMemo(
    () =>
      suggested.filter(
        (s) =>
          s.confidence >= confidenceThreshold &&
          !suggestedRemoved.has(chipKey(s)) &&
          !presetIds.has(chipKey(s)) &&
          !manualIds.has(chipKey(s)) &&
          !(s.objectRecordId !== null && excludedIds.has(s.objectRecordId))
      ),
    [
      suggested,
      confidenceThreshold,
      suggestedRemoved,
      presetIds,
      manualIds,
      excludedIds,
    ]
  );

  const selectedAssociations = useMemo<SelectedAssociation[]>(() => {
    const seen = new Set<string>();
    const result: SelectedAssociation[] = [];
    for (const chip of [...presetChips, ...suggestedChips, ...manual]) {
      if (seen.has(chipKey(chip))) continue;
      seen.add(chipKey(chip));
      result.push({
        objectTypeId: chip.objectTypeId,
        objectRecordId: chip.objectRecordId,
      });
    }

    return result;
  }, [presetChips, suggestedChips, manual]);

  return {
    presetChips,
    suggestedChips,
    manualChips: manual,
    selectedAssociations,
    add(selection) {
      const key = chipKey(selection);
      const alreadyPresent =
        presetChips.some((p) => chipKey(p) === key) ||
        suggestedChips.some((s) => chipKey(s) === key) ||
        manual.some((m) => chipKey(m) === key);
      if (!alreadyPresent) {
        setManual((prev) => [...prev, selection]);
      }
    },
    removePreset(key) {
      setPresetRemoved((prev) => new Set(prev).add(key));
    },
    removeSuggested(key) {
      setSuggestedRemoved((prev) => new Set(prev).add(key));
    },
    removeManual(key) {
      setManual((prev) => prev.filter((m) => chipKey(m) !== key));
    },
  };
}

type AssociationSelectionProps = {
  controller: AssociationSelectionController;
  /**
   * Constrains the picker (browse, search hints, inline-create) to these object
   * types. Unset lists every type. Used by the sidebar Record step to limit the
   * smart-select to the object types the chosen record type references.
   */
  allowedObjectTypeIds?: string[];
};

export function AssociationSelection({
  controller,
  allowedObjectTypeIds,
}: AssociationSelectionProps) {
  const {
    presetChips,
    suggestedChips,
    manualChips,
    add,
    removePreset,
    removeSuggested,
    removeManual,
  } = controller;

  // Quick-create: a record minted from the picker is staged like any manual
  // pick, so it commits with the note/file.
  const inlineCreate = useInlineCreateAssociation(add);

  return (
    <div>
      <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-neutral-500">
        Associations
      </div>
      <AssociationPicker
        allowCategorySelection
        onAdd={add}
        placeholder="About…"
        inlineCreate={inlineCreate}
        allowedObjectTypeIds={allowedObjectTypeIds}
      />

      {presetChips.length > 0 && (
        <div className="mt-2.5">
          <div className="mb-1 flex items-center gap-1 text-xs text-neutral-500">
            <Sparkles className="h-3 w-3 text-primary-500" />
            From scope — keep, or tap × to remove
          </div>
          <div className="flex flex-wrap gap-1.5">
            {presetChips.map((assoc) => (
              <AssocChip
                key={chipKey(assoc)}
                label={assoc.objectRecordLabel}
                typeName={assoc.objectTypeName}
                suggested
                onRemove={() => {
                  removePreset(chipKey(assoc));
                }}
              />
            ))}
          </div>
        </div>
      )}

      {suggestedChips.length > 0 && (
        <div className="mt-2.5">
          <div className="mb-1 flex items-center gap-1 text-xs text-neutral-500">
            <Sparkles className="h-3 w-3 text-primary-500" />
            Suggested from this file — keep, or tap × to remove
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggestedChips.map((assoc) => (
              <AssocChip
                key={chipKey(assoc)}
                label={assoc.objectRecordLabel}
                typeName={assoc.objectTypeName}
                suggested
                onRemove={() => {
                  removeSuggested(chipKey(assoc));
                }}
              />
            ))}
          </div>
        </div>
      )}

      {manualChips.length > 0 && (
        <div
          className={cn(
            'flex flex-wrap gap-1.5',
            (presetChips.length > 0 || suggestedChips.length > 0) && 'mt-2'
          )}
        >
          {manualChips.map((assoc) => (
            <AssocChip
              key={chipKey(assoc)}
              label={assoc.objectRecordLabel}
              typeName={assoc.objectTypeName}
              onRemove={() => {
                removeManual(chipKey(assoc));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type AssocChipProps = {
  label: string;
  typeName: string;
  suggested?: boolean;
  onRemove: () => void;
};

function AssocChip({label, typeName, suggested, onRemove}: AssocChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border py-0.5 pl-2 pr-1 text-xs',
        suggested
          ? 'border-dashed border-primary-300 bg-primary-50 text-primary-700'
          : 'border-neutral-200 bg-neutral-100 text-neutral-700'
      )}
    >
      <span className="text-xs font-medium text-neutral-400">{typeName}</span>
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 shrink-0 text-neutral-400 hover:text-neutral-700"
        aria-label={`Remove ${label}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
